import { mergeParsers, type Parser } from "../parsing/index.js";
import { doesPathStartsWithPattern } from "./pathParser.js";
import type Route from "./Route.js";
import type {
    AnyPathParams,
    AnyQueryParams,
    Inherit,
    NoPathParams,
    NoQueryParams,
    PathParamsBase,
    QueryParamsBase,
} from "./types.js";
import { joinPaths } from "./utilities.js";

export default class RouteNavigator<
    PathParams extends PathParamsBase | NoPathParams = NoPathParams,
    QueryParams extends QueryParamsBase | NoQueryParams = NoQueryParams,
> {
    path: string | null = null;
    pathParamsSchema: Parser<PathParams> | null = null;
    queryParamsSchema: Parser<QueryParams> | null = null;
    parent: RouteNavigator<AnyPathParams, AnyQueryParams> | undefined;

    // Definitions methods

    setPath(path: string): RouteNavigator<NoQueryParams, QueryParams>;
    setPath<S extends PathParamsBase>(
        path: string,
        schema: Parser<S>,
    ): RouteNavigator<S, QueryParams>;
    setPath<S extends PathParamsBase>(path: string, schema?: Parser<S>) {
        if (!schema) {
            const nav = this.clone<NoQueryParams, QueryParams>();
            nav.path = path;
            nav.pathParamsSchema = null;
            return nav;
        } else {
            const nav = this.clone<S, QueryParams>();
            nav.path = path;
            nav.pathParamsSchema = schema;
            return nav;
        }
    }

    setQueryParamsSchema<S extends QueryParamsBase>(schema: Parser<S>) {
        const route = this.clone<PathParams, S>();
        route.queryParamsSchema = schema;
        return route;
    }

    setNavigator<
        ParentPathParams extends PathParamsBase | never,
        ParentQueryParams extends QueryParamsBase | never,
    >(
        navigator: RouteNavigator<ParentPathParams, ParentQueryParams>,
    ): RouteNavigator<
        Inherit<ParentPathParams, PathParams>,
        Inherit<ParentQueryParams, QueryParams>
    > {
        const route = this.clone<
            Inherit<ParentPathParams, PathParams>,
            Inherit<ParentQueryParams, QueryParams>
        >();
        route.path = joinPaths(navigator.path, this.path);
        route.pathParamsSchema = mergeParsers(
            navigator.pathParamsSchema,
            this.pathParamsSchema,
        ) as Parser<Inherit<ParentPathParams, PathParams>>;
        route.queryParamsSchema = mergeParsers(
            navigator.queryParamsSchema,
            this.queryParamsSchema,
        ) as Parser<Inherit<ParentQueryParams, QueryParams>>;
        route.parent = navigator;
        return route;
    }

    private clone<
        NewPathParams extends PathParamsBase | never,
        NewQueryParams extends QueryParamsBase | never,
    >(): RouteNavigator<NewPathParams, NewQueryParams> {
        const route = new RouteNavigator<NewPathParams, NewQueryParams>();
        route.path = this.path;
        route.pathParamsSchema = this.pathParamsSchema as RouteNavigator<
            NewPathParams,
            NewQueryParams
        >["pathParamsSchema"];
        route.queryParamsSchema = this.queryParamsSchema as RouteNavigator<
            NewPathParams,
            NewQueryParams
        >["queryParamsSchema"];
        route.parent = this.parent;
        return route;
    }

    // Attaching

    addRoute<RoutePathParams extends PathParamsBase, RouteQueryParams extends QueryParamsBase>(
        route: Route<RoutePathParams, RouteQueryParams>,
    ) {
        return route.setNavigator(this) as Route<
            Inherit<PathParams, RoutePathParams>,
            Inherit<QueryParams, RouteQueryParams>
        >;
    }

    addNavigator<
        NavigatorPathParams extends PathParamsBase,
        NavigatorQueryParams extends QueryParamsBase,
    >(navigator: RouteNavigator<NavigatorPathParams, NavigatorQueryParams>) {
        return navigator.setNavigator(this) as RouteNavigator<
            Inherit<PathParams, NavigatorPathParams>,
            Inherit<QueryParams, NavigatorQueryParams>
        >;
    }

    // Reading

    private getPathPattern() {
        if (!this.path) {
            return "/";
        } else {
            return this.path;
        }
    }

    matches(pathname: string): boolean {
        const matching = doesPathStartsWithPattern(pathname, this.getPathPattern());
        return matching !== false;
    }

    // get path(): string {
    //     return joinPaths(this.parent?.path ?? "", this.relativePath);
    // }

    // matches(pathname: string): boolean {
    //     return matchPath(this.path, pathname, "prefix");
    // }

    // lock() {
    //     this.locked = true;
    //     for (const route of this.routes) route.lock();
    //     for (const navigator of this.navigators) navigator.lock();
    // }

    // setPath(path: string): RouteNavigator<unknown, QueryParams>;
    // setPath<S extends PathParamsBase>(
    //     path: string,
    //     schema: Parser<S>,
    // ): RouteNavigator<S, QueryParams>;
    // setPath<S extends PathParamsBase>(path: string, schema?: Parser<S>) {
    //     const navigator = this.clone<S, QueryParams>();
    //     navigator.relativePath = path;
    //     navigator.pathParamsSchema = schema;
    //     return navigator;
    // }

    // setQueryParamsSchema<S extends QueryParamsBase>(schema: Parser<S>) {
    //     const navigator = this.clone<PathParams, S>();
    //     navigator.queryParamsSchema = schema;
    //     return navigator;
    // }

    // addRoute<P extends PathParamsBase | unknown, Q extends QueryParamsBase | unknown>(
    //     route: Route<P, Q>,
    // ): Route<Inherit<PathParams, P>, Inherit<QueryParams, Q>> {
    //     if (this.locked) throw new Error("RouteNavigator is locked");
    //     return route.attachTo(this);
    // }

    // addNavigator<P extends PathParamsBase | unknown, Q extends QueryParamsBase | unknown>(
    //     navigator: RouteNavigator<P, Q>,
    // ): RouteNavigator<Inherit<PathParams, P>, Inherit<QueryParams, Q>> {
    //     if (this.locked) throw new Error("RouteNavigator is locked");
    //     return navigator.attachTo(this);
    // }

    // attachTo<
    //     ParentPathParams extends PathParamsBase | unknown,
    //     ParentQueryParams extends QueryParamsBase | unknown,
    // >(
    //     navigator: RouteNavigator<ParentPathParams, ParentQueryParams>,
    // ): RouteNavigator<
    //     Inherit<ParentPathParams, PathParams>,
    //     Inherit<ParentQueryParams, QueryParams>
    // > {
    //     if (this.locked) throw new Error("RouteNavigator is locked");
    //     if (navigator.locked) throw new Error("RouteNavigator is locked");
    //     if (this.parent) throw new Error("RouteNavigator is already attached to a navigator");
    //     let ancestor: RouteNavigator<unknown, unknown> | undefined = navigator as RouteNavigator<
    //         unknown,
    //         unknown
    //     >;
    //     while (ancestor) {
    //         if (ancestor === this) {
    //             throw new Error("Cannot attach a navigator to itself or one of its descendants");
    //         }
    //         ancestor = ancestor.parent;
    //     }
    //     const attached = this.clone<
    //         Inherit<ParentPathParams, PathParams>,
    //         Inherit<ParentQueryParams, QueryParams>
    //     >();
    //     attached.parent = navigator as RouteNavigator<unknown, unknown>;
    //     navigator.registerNavigator(attached as RouteNavigator<unknown, unknown>);
    //     return attached;
    // }

    // getQueryParamsParsers(): Parser<unknown>[] {
    //     const parsers = this.parent?.getQueryParamsParsers() ?? [];
    //     if (this.queryParamsSchema) parsers.push(this.queryParamsSchema as Parser<unknown>);
    //     return parsers;
    // }

    // getPathParamsParsers(): Parser<unknown>[] {
    //     const parsers = this.parent?.getPathParamsParsers() ?? [];
    //     if (this.pathParamsSchema) parsers.push(this.pathParamsSchema as Parser<unknown>);
    //     return parsers;
    // }

    // getRoutes(): CollectedRoute[] {
    //     return [...this.navigators.flatMap((navigator) => navigator.getRoutes()), ...this.routes];
    // }

    // registerRoute(route: CollectedRoute) {
    //     this.routes.push(route);
    // }

    // replaceRoute(previous: CollectedRoute, next: CollectedRoute) {
    //     const index = this.routes.indexOf(previous);
    //     if (index >= 0) this.routes[index] = next;
    // }

    // registerNavigator(navigator: RouteNavigator<unknown, unknown>) {
    //     this.navigators.push(navigator);
    // }

    // replaceNavigator(
    //     previous: RouteNavigator<unknown, unknown>,
    //     next: RouteNavigator<unknown, unknown>,
    // ) {
    //     const index = this.navigators.indexOf(previous);
    //     if (index >= 0) this.navigators[index] = next;
    // }

    // private clone<
    //     NewPathParams extends PathParamsBase | unknown,
    //     NewQueryParams extends QueryParamsBase | unknown,
    // >(): RouteNavigator<NewPathParams, NewQueryParams> {
    //     if (this.locked) throw new Error("RouteNavigator is locked");
    //     if (this.parent?.locked) throw new Error("RouteNavigator is locked");
    //     const navigator = new RouteNavigator<NewPathParams, NewQueryParams>();
    //     navigator.relativePath = this.relativePath;
    //     navigator.pathParamsSchema = this.pathParamsSchema as RouteNavigator<
    //         NewPathParams,
    //         NewQueryParams
    //     >["pathParamsSchema"];
    //     navigator.queryParamsSchema = this.queryParamsSchema as RouteNavigator<
    //         NewPathParams,
    //         NewQueryParams
    //     >["queryParamsSchema"];
    //     navigator.parent = this.parent;
    //     this.parent?.replaceNavigator(
    //         this as RouteNavigator<unknown, unknown>,
    //         navigator as RouteNavigator<unknown, unknown>,
    //     );
    //     return navigator;
    // }
}
